import type { Layer } from '../../../../context/context';
import type { Vehicle } from '../../../../types/vehicle';
import { analyzeTripsLayer, validateTripLayer } from './tripAnalysis';
import { ShiftGenerator } from './shiftGenerator';
import type { GeneratorConfig, PeakPeriod, GeneratedShift } from './types';
import { calculateTimeDifference, parseTime } from './timeUtils';

type ServicePreset = 'on-demand' | 'school' | 'paratransit' | null | undefined;

export interface TripLayerFleetGenerationInput {
  layer: Layer;
  centroidPoint: { pointId: string; coordinates: { lat: number; lng: number } };
  capacityStandard: number;
  capacityWheelchair: number;
  servicePreset?: ServicePreset;
  selectedLayerId?: string;
}

export interface TripLayerFleetGenerationOutput {
  config: GeneratorConfig;
  peakPeriods: PeakPeriod[];
  shifts: GeneratedShift[];
  vehicles: Vehicle[];
  summary: {
    totalVehicles: number;
    totalShifts: number;
    totalBreaks: number;
    servicePeriod: string;
    totalStandardSeats: number;
    totalWheelchairSeats: number;
    totalCapacity: number;
    activeVehicleHours: number;
    totalVehicleHours: number;
    utilizationPercent: number;
  };
}

export function getTripsPerVehiclePerHourForPreset(servicePreset?: ServicePreset): number {
  return servicePreset === 'paratransit' ? 3 : 5;
}

export function generateFleetFromTripLayerAnalysis(input: TripLayerFleetGenerationInput): TripLayerFleetGenerationOutput {
  const validation = validateTripLayer(input.layer);
  if (!validation.isValid) {
    throw new Error(validation.error || 'Trip layer is not valid for fleet analysis.');
  }

  const standardSeats = Math.max(0, Math.round(input.capacityStandard));
  const wheelchairSeats = Math.max(0, Math.round(input.capacityWheelchair));
  if (standardSeats + wheelchairSeats <= 0) {
    throw new Error('Vehicle capacity must be greater than zero.');
  }

  const tripsPerVehiclePerHour = getTripsPerVehiclePerHourForPreset(input.servicePreset);
  const tripAnalysis = analyzeTripsLayer(input.layer, tripsPerVehiclePerHour);

  const peakPeriods = (tripAnalysis.detectedPeakPeriods || []).map((peak) => ({
    start: peak.start,
    end: peak.end,
  }));

  const config: GeneratorConfig = {
    mode: 'trip-layer',
    selectedLayerId: input.selectedLayerId || String(input.layer.layerId),
    manualVehicleCount: Math.max(1, tripAnalysis.calculatedVehicles),
    selectedRouteIds: [],
    vehicleSharingMode: 'shared',
    serviceStartTime: tripAnalysis.suggestedServiceStart || '06:00',
    serviceEndTime: tripAnalysis.suggestedServiceEnd || '22:00',
    maxShiftTimeMinutes: 480,
    breakLengthMinutes: 30,
    minTimeBeforeBreakMinutes: 120,
    minTimeAfterBreakMinutes: 120,
    standardSeats,
    wheelchairSeats,
    startLocationId: input.centroidPoint.pointId,
    endLocationId: input.centroidPoint.pointId,
    peakPeriods,
    breakReductionDuringPeakMinutes: 0,
    staggerBreaks: true,
    tripsPerVehiclePerHour,
    demandCurve: tripAnalysis.hourlyDemand,
    hourlyVehicleRequirements: tripAnalysis.hourlyVehicleRequirements,
    useDemandBasedAllocation: true,
    flexibilityPercent: 0,
    enableShiftAllocation: false,
  };

  const generator = new ShiftGenerator(config);
  let shifts = generator.generateShifts();
  if (shifts.length === 0 && config.useDemandBasedAllocation) {
    const fallbackGenerator = new ShiftGenerator({
      ...config,
      useDemandBasedAllocation: false,
    });
    shifts = fallbackGenerator.generateShifts();
  }
  if (shifts.length === 0) {
    throw new Error('Fleet generation returned no shifts. Adjust demand or settings and try again.');
  }

  const vehicles: Vehicle[] = shifts.map((shift, index) => ({
    id: shift.vehicleId,
    name: shift.vehicleName,
    standardSeats,
    wheelchairSeats,
    operatingHours: shift.operatingPeriods,
    startLocation: input.centroidPoint.pointId,
    endLocation: input.centroidPoint.pointId,
    startLocationCoordinates: input.centroidPoint.coordinates,
    endLocationCoordinates: input.centroidPoint.coordinates,
    sortOrder: index + 1,
  }));

  const totalBreaks = shifts.reduce(
    (sum, shift) => sum + shift.operatingPeriods.reduce((inner, period) => inner + (period.breaks?.length || 0), 0),
    0
  );
  let activeMinutes = 0;
  shifts.forEach((shift) => {
    shift.operatingPeriods.forEach((period) => {
      activeMinutes += calculateTimeDifference(parseTime(period.start), parseTime(period.end));
      (period.breaks || []).forEach((breakPeriod) => {
        activeMinutes -= calculateTimeDifference(parseTime(breakPeriod.start), parseTime(breakPeriod.end));
      });
    });
  });

  const serviceDuration = calculateTimeDifference(parseTime(config.serviceStartTime), parseTime(config.serviceEndTime));
  const totalVehicleHours = (serviceDuration * vehicles.length) / 60;
  const activeVehicleHours = activeMinutes / 60;
  const utilizationPercent = totalVehicleHours > 0 ? (activeVehicleHours / totalVehicleHours) * 100 : 0;

  return {
    config,
    peakPeriods,
    shifts,
    vehicles,
    summary: {
      totalVehicles: vehicles.length,
      totalShifts: shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0),
      totalBreaks,
      servicePeriod: `${config.serviceStartTime} - ${config.serviceEndTime}`,
      totalStandardSeats: vehicles.length * standardSeats,
      totalWheelchairSeats: vehicles.length * wheelchairSeats,
      totalCapacity: vehicles.length * (standardSeats + wheelchairSeats),
      activeVehicleHours: Math.round(activeVehicleHours * 10) / 10,
      totalVehicleHours: Math.round(totalVehicleHours * 10) / 10,
      utilizationPercent: Math.round(utilizationPercent * 10) / 10,
    },
  };
}
